import { useState } from "react";
import { Modal, Btn, Input } from "@/components/Kit";
import { Plus, Trash2, PackagePlus, ChevronDown } from "lucide-react";

const MAQUINAS = ["Offset 71", "Offset 102", "Epson S40600", "Ricoh 5210", "Ricoh 7200", "Corte Mimaki", "Corte Guillotina", "Laminadora"];

export function OrderItemsEditor({ items, setItems, products = [], onCreateProduct }) {
  const [prodOpen, setProdOpen] = useState(false);
  const [pForm, setPForm] = useState({ name: "", unit: "pieza", unit_price: 0, sku: "" });

  const update = (idx, key, val) => {
    setItems(items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  };
  const add = () => setItems([...items, { description: "", quantity: 1, product_id: null, maquina: "", acabados: "", material: "" }]);
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));

  const addFromCatalog = (pid) => {
    if (!pid) return;
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setItems([...items, { description: p.name, quantity: 1, product_id: p.id, maquina: "", acabados: "", material: "" }]);
  };

  const createProduct = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const created = await onCreateProduct({ ...pForm, unit_price: Number(pForm.unit_price) || 0, active: true });
    if (created) {
      setItems([...items, { description: created.name, quantity: 1, product_id: created.id, maquina: "", acabados: "", material: "" }]);
    }
    setProdOpen(false);
    setPForm({ name: "", unit: "pieza", unit_price: 0, sku: "" });
  };

  const active = products.filter((p) => p.active !== false);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="overline">Conceptos</div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value=""
              data-testid="order-catalog-select"
              onChange={(e) => addFromCatalog(e.target.value)}
              className="appearance-none bg-white border border-[#E3E8EE] pl-3 pr-7 py-1.5 font-mono-data text-xs focus:outline-none focus:ring-1 focus:ring-[#465B7F] cursor-pointer"
            >
              <option value="">+ Del catálogo</option>
              {active.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[#5B6672]" />
          </div>
          <button
            type="button"
            onClick={() => setProdOpen(true)}
            data-testid="order-new-product-inline-button"
            className="inline-flex items-center gap-1.5 border border-[#465B7F] text-[#465B7F] px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-[#465B7F] hover:text-white transition-colors"
          >
            <PackagePlus size={13} /> Nuevo producto
          </button>
        </div>
      </div>

      <div className="border border-[#E3E8EE]">
        <div className="grid grid-cols-12 gap-2 bg-[#465B7F] px-3 py-2.5 text-xs uppercase tracking-widest font-bold text-white">
          <div className="col-span-9">Descripción</div>
          <div className="col-span-3 text-right">Cantidad</div>
        </div>
        {items.map((it, idx) => (
          <div key={idx} className="border-t border-[#E3E8EE] px-3 py-2 space-y-2">
            <div className="grid grid-cols-12 gap-2 items-start">
              <textarea
                rows={2}
                className="col-span-9 bg-white border border-[#E3E8EE] font-mono-data text-sm px-2 py-1 leading-snug resize-y min-h-[38px] focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
                placeholder="Servicio / concepto"
                value={it.description}
                data-testid={`order-item-desc-${idx}`}
                onChange={(e) => update(idx, "description", e.target.value)}
              />
              <div className="col-span-3 flex items-center justify-end gap-2">
                <input
                  type="number" step="any"
                  className="w-20 bg-transparent font-mono-data text-sm py-1 text-right focus:outline-none border-b border-transparent focus:border-[#465B7F]"
                  value={it.quantity}
                  data-testid={`order-item-qty-${idx}`}
                  onChange={(e) => update(idx, "quantity", parseFloat(e.target.value) || 0)}
                />
                <button type="button" onClick={() => remove(idx)} className="text-[#5B6672] hover:text-[#ED1F26]" data-testid={`order-item-remove-${idx}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <label className="block">
                <span className="block font-mono-data text-[10px] uppercase tracking-wider text-[#8A94A0] mb-1">Máquina</span>
                <select
                  value={it.maquina || ""}
                  onChange={(e) => update(idx, "maquina", e.target.value)}
                  data-testid={`order-item-maquina-${idx}`}
                  className="w-full bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-xs focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
                >
                  <option value="">Sin especificar</option>
                  {MAQUINAS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block font-mono-data text-[10px] uppercase tracking-wider text-[#8A94A0] mb-1">Acabados</span>
                <input
                  type="text"
                  value={it.acabados || ""}
                  onChange={(e) => update(idx, "acabados", e.target.value)}
                  data-testid={`order-item-acabados-${idx}`}
                  placeholder="Ej. Laminado mate, suaje"
                  className="w-full bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-xs focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
                />
              </label>
              <label className="block">
                <span className="block font-mono-data text-[10px] uppercase tracking-wider text-[#8A94A0] mb-1">Material</span>
                <input
                  type="text"
                  value={it.material || ""}
                  onChange={(e) => update(idx, "material", e.target.value)}
                  data-testid={`order-item-material-${idx}`}
                  placeholder="Ej. Couché 300g"
                  className="w-full bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-xs focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
                />
              </label>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          data-testid="order-add-item-button"
          className="flex items-center gap-2 px-3 py-2 border-t border-[#E3E8EE] text-sm text-[#465B7F] hover:bg-[#465B7F]/5 w-full transition-colors"
        >
          <Plus size={14} /> Agregar concepto
        </button>
      </div>

      <Modal open={prodOpen} onClose={() => setProdOpen(false)} title="Nuevo producto">
        <form onSubmit={createProduct} className="space-y-4">
          <Input label="Nombre *" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} required testid="order-quick-product-name" />
          <div className="grid grid-cols-3 gap-4">
            <Input label="SKU" value={pForm.sku} onChange={(e) => setPForm({ ...pForm, sku: e.target.value })} testid="order-quick-product-sku" />
            <Input label="Unidad" value={pForm.unit} onChange={(e) => setPForm({ ...pForm, unit: e.target.value })} testid="order-quick-product-unit" />
            <Input label="Precio *" type="number" step="any" value={pForm.unit_price} onChange={(e) => setPForm({ ...pForm, unit_price: e.target.value })} required testid="order-quick-product-price" />
          </div>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setProdOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="order-save-quick-product-button">Crear y agregar</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
